import type { ReactElement, ReactNode } from 'react';
import {
  Body,
  Box,
  Button,
  Checkbox,
  I,
  Input,
  Pill,
  Row,
  Select,
  Stack,
} from '@leanin/ui';
import type { SpaceKey } from '@leanin/ui';
import {
  MAX_PROMPT_CHARS,
  type IntakeQuestionType,
} from '@leanin/shared/intake';
import type { DraftQuestion } from './intake-builder-model';
import { QuestionEditor } from './QuestionEditor';

type TypeOption = { readonly value: IntakeQuestionType; readonly label: string };

type Props = {
  readonly prompt: string;
  readonly typeLabel: string;
  readonly helper?: ReactNode;
  /** Dims the prompt for an available-but-not-added recommended row. */
  readonly muted?: boolean;
  readonly checked: boolean;
  readonly onToggleChecked: (checked: boolean) => void;
  readonly checkboxDisabled?: boolean;
  readonly checkboxLabel: string;
  /** Real drag handle from the Sortable primitive (draggable rows). */
  readonly dragHandle?: ReactElement;
  /** Render an inert six-dot placeholder for alignment (unselected rows). */
  readonly handlePlaceholder?: boolean;
  // --- in-place editing (real questions only) ---
  readonly question?: DraftQuestion;
  readonly editing?: boolean;
  readonly onEdit?: () => void;
  readonly editLabel?: string;
  readonly collapseLabel?: string;
  readonly promptPlaceholder?: string;
  readonly typeSelectLabel?: string;
  /** When true, the answer type is editable via a dropdown; else shown as a static pill. */
  readonly allowTypeChange?: boolean;
  readonly typeOptions?: readonly TypeOption[];
  readonly onPatch?: (patch: Partial<DraftQuestion>) => void;
  readonly onChangeType?: (type: IntakeQuestionType) => void;
  readonly errorMessage?: string | null;
  // --- remove ---
  readonly onRemove?: () => void;
  readonly removeLabel?: string;
  // --- styling ---
  readonly tinted?: boolean;
  readonly rounded?: boolean;
  readonly divider?: boolean;
  readonly padX?: SpaceKey;
  readonly padY?: SpaceKey;
  readonly accentPill?: boolean;
};

// Inert grip glyph matching the Sortable drag handle, for unselected recommended
// rows so they stay aligned with the draggable rows. Visually disabled.
function HandlePlaceholder() {
  return (
    <span
      aria-hidden
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 44,
        color: 'var(--muted-foreground)',
        opacity: 0.35,
      }}
    >
      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
        <circle cx="5" cy="4" r="1.5" />
        <circle cx="11" cy="4" r="1.5" />
        <circle cx="5" cy="8" r="1.5" />
        <circle cx="11" cy="8" r="1.5" />
        <circle cx="5" cy="12" r="1.5" />
        <circle cx="11" cy="12" r="1.5" />
      </svg>
    </span>
  );
}

// One question in the picker. Collapsed it is a compact row; editing it becomes
// the in-place editor — the SAME card, with the wording turning into a text
// input and the answer-type pill into a dropdown, and type-specific controls
// revealed beneath. The wording is never shown twice, and no separate editor
// section is appended below the row.
export function QuestionRow({
  prompt,
  typeLabel,
  helper,
  muted,
  checked,
  onToggleChecked,
  checkboxDisabled,
  checkboxLabel,
  dragHandle,
  handlePlaceholder,
  question,
  editing,
  onEdit,
  editLabel,
  collapseLabel,
  promptPlaceholder,
  typeSelectLabel,
  allowTypeChange,
  typeOptions,
  onPatch,
  onChangeType,
  errorMessage,
  onRemove,
  removeLabel,
  tinted,
  rounded,
  divider,
  padX = 'md',
  padY = 'sm',
  accentPill,
}: Props) {
  const isEditing = editing === true && question !== undefined;
  const handle = dragHandle ?? (handlePlaceholder ? <HandlePlaceholder /> : null);

  return (
    <Box
      {...(rounded ? { rounded: 'lg' } : {})}
      style={{
        ...(tinted ? { background: 'var(--light-poppy)' } : {}),
        ...(divider ? { borderTop: '1px solid var(--border)' } : {}),
      }}
    >
      <Row align="center" gap="sm" px={padX} py={padY}>
        {handle}
        <Checkbox
          checked={checked}
          onCheckedChange={(v) => onToggleChecked(v === true)}
          disabled={checkboxDisabled}
          aria-label={checkboxLabel}
        />

        <Stack gap="xs" style={{ flex: 1, minWidth: 0 }}>
          {isEditing && question !== undefined ? (
            <Input
              autoFocus
              value={question.prompt}
              maxLength={MAX_PROMPT_CHARS}
              placeholder={promptPlaceholder}
              invalid={errorMessage != null}
              aria-label={promptPlaceholder}
              onChange={(e) => onPatch?.({ prompt: e.target.value })}
            />
          ) : (
            <Body color={muted ? 'mute' : undefined}>{prompt}</Body>
          )}
          {isEditing && errorMessage != null ? (
            <Body size="sm" color="destructive">
              {errorMessage}
            </Body>
          ) : null}
          {helper}
        </Stack>

        {isEditing && allowTypeChange && question !== undefined && typeOptions ? (
          <div style={{ width: 152, flexShrink: 0 }}>
            <Select
              value={question.type}
              aria-label={typeSelectLabel}
              options={typeOptions.map((o) => ({ value: o.value, label: o.label }))}
              onChange={(e) => onChangeType?.(e.target.value as IntakeQuestionType)}
              style={{ width: '100%' }}
            />
          </div>
        ) : (
          <Pill
            size="xs"
            style={{ alignSelf: 'center' }}
            {...(accentPill ? { tone: 'burgundy' as const } : {})}
          >
            {typeLabel}
          </Pill>
        )}

        {onEdit && (
          <Button
            variant="ghost"
            size="sm"
            icon={I.edit}
            aria-label={isEditing ? collapseLabel : editLabel}
            onClick={onEdit}
          />
        )}
        {onRemove && (
          <Button
            variant="ghost"
            size="sm"
            icon={I.x}
            aria-label={removeLabel}
            onClick={onRemove}
          />
        )}
      </Row>

      {isEditing && question !== undefined && onPatch ? (
        <Box px={padX} pb={padY}>
          <QuestionEditor question={question} onPatch={onPatch} />
        </Box>
      ) : null}
    </Box>
  );
}
